/**
 * @fileoverview Shared conversion between the inspection module's ContactEntry
 *   shape and the standard ContactsPanel's PanelContact / EntityContactLink shapes.
 * @module ui-admin/contacts-module
 *
 * Responsibilities:
 * - Convert ContactEntry → EntityContactLink so the standard ContactsPanel can
 *   render inspection-module contacts without a parallel UI.
 * - Convert PanelContact → ContactEntry so add/select actions from ContactsPanel
 *   can be tracked in the inspection session's ContactEntry[] list.
 * - Convert ContactFormValues → ContactEntry for edit operations.
 *
 * Dependencies:
 * - @firesquirrel/core-domain (ContactEntry, PhoneRow, EmailRow, ContactFormValues)
 * - ../ContactsPanel (PanelContact, EntityContactLink, NewContactInput)
 *
 * Architecture:
 * - Pure functions; no side effects, no state, no I/O.
 * - The single source of truth for shape translation between the inspection
 *   module contract and the standard contact entry surface.
 *
 * Notes:
 * - These converters exist because the inspection module uses ContactEntry
 *   (a flat, session-oriented shape) while ContactsPanel uses PanelContact
 *   (a Dexie-oriented shape with _id key path). The conversion is lossless
 *   for all fields used by both surfaces.
 *
 * @reusable
 * Usage: Called by ContactsModulePanel and host adapters to bridge shapes.
 * Rules: Pure functions only; never mutate input arguments.
 */

import type {
  ContactEntry,
  PhoneRow,
  EmailRow,
  ContactFormValues,
  PhoneEntry,
  EmailEntry,
  PhoneType,
  EmailType,
} from '@firesquirrel/core-domain';
import type {
  PanelContact,
  EntityContactLink,
  NewContactInput,
} from '../ContactsPanel';

import { resolveLookupDisplayValue, PHONE_TYPES, EMAIL_TYPES } from '@firesquirrel/core-domain';

const phoneTypesSet = new Set<string>(PHONE_TYPES);
const emailTypesSet = new Set<string>(EMAIL_TYPES);

/**
 * Convert a PanelContact (from the Dexie-backed pool) into a ContactEntry
 * (the inspection session's contact shape). Used when the inspector selects
 * an existing system contact from the standard ContactsPanel search.
 *
 * @param pc The pool contact to convert.
 * @param assignedRoles Optional roles to assign; falls back to the contact's
 *   defaultRoles, then to the provided fallbackRole.
 * @param fallbackRole Role to use when no roles are available from the contact.
 * @returns A ContactEntry suitable for the inspection session list.
 */
export function panelContactToEntry(
  pc: PanelContact,
  assignedRoles: string[] = [],
  fallbackRole = '',
): ContactEntry {
  const phones: PhoneRow[] = (pc.phoneNumbers ?? []).map((p) => ({
    id: p._id,
    number: p.number,
    label: resolveLookupDisplayValue(p.type, true),
  }));
  const emails: EmailRow[] = (pc.emails ?? []).map((e) => ({
    id: e._id,
    address: e.email,
    label: resolveLookupDisplayValue(e.type, true),
  }));
  const primaryPhone = pc.phoneNumbers?.find((p) => p.isPrimary)?.number
    ?? pc.phoneNumbers?.[0]?.number
    ?? '';
  const primaryEmail = pc.emails?.find((e) => e.isPrimary)?.email
    ?? pc.emails?.[0]?.email
    ?? '';
  const roles = assignedRoles.length
    ? assignedRoles
    : pc.defaultRoles?.length
      ? pc.defaultRoles.map((r: any) => resolveLookupDisplayValue(r)).filter(Boolean)
      : fallbackRole
        ? [fallbackRole]
        : [];

  return {
    id: pc._id,
    roles,
    name: pc.name,
    phones,
    emails,
    phone: primaryPhone,
    email: primaryEmail,
  };
}

/**
 * Convert a ContactEntry (from the inspection session list) into an
 * EntityContactLink so the standard ContactsPanel can render it. The roles
 * from the ContactEntry are preserved as the EntityContactLink's roles field.
 *
 * @param entry The inspection session contact to convert.
 * @param poolContact Optional PanelContact from the contact pool — when
 *   available, company/title/address are sourced from it so the panel
 *   displays the full contact info that ContactEntry doesn't carry.
 * @returns An EntityContactLink suitable for ContactsPanel's contacts prop.
 */
export function entryToEntityContactLink(
  entry: ContactEntry,
  poolContact?: PanelContact,
): EntityContactLink {
  const phoneTypesSet = new Set<string>(PHONE_TYPES);
  const emailTypesSet = new Set<string>(EMAIL_TYPES);

  return {
    _id: entry.id,
    name: entry.name,
    defaultRoles: entry.roles,
    roles: entry.roles,
    company: poolContact?.company ?? '',
    title: poolContact?.title ?? '',
    phoneNumbers: (entry.phones ?? []).map((p) => ({
      _id: p.id,
      number: p.number,
      type: (phoneTypesSet.has(p.label) ? p.label : 'Mobile') as PhoneType,
      isPrimary: false,
    })) as PhoneEntry[],
    emails: (entry.emails ?? []).map((e) => ({
      _id: e.id,
      email: e.address,
      type: (emailTypesSet.has(e.label) ? e.label : 'Business') as EmailType,
      isPrimary: false,
    })) as EmailEntry[],
    address: poolContact?.address ?? '',
  };
}

/**
 * Convert a ContactEntry into a NewContactInput for the standard
 * ContactsPanel's create flow. Used when a session-mode contact needs to be
 * persisted through the standard create path.
 *
 * @param entry The contact entry to convert.
 * @returns A NewContactInput for ContactsPanel's onCreate callback.
 */
export function entryToNewContactInput(entry: ContactEntry): NewContactInput {
  const phoneTypesSet = new Set<string>(PHONE_TYPES);
  const emailTypesSet = new Set<string>(EMAIL_TYPES);

  return {
    name: entry.name,
    defaultRoles: entry.roles,
    phoneNumbers: (entry.phones ?? []).map((p) => ({
      _id: p.id,
      number: p.number,
      type: (phoneTypesSet.has(p.label) ? p.label : 'Mobile') as PhoneType,
      isPrimary: false,
    })) as PhoneEntry[],
    emails: (entry.emails ?? []).map((e) => ({
      _id: e.id,
      email: e.address,
      type: (emailTypesSet.has(e.label) ? e.label : 'Business') as EmailType,
      isPrimary: false,
    })) as EmailEntry[],
  };
}

/**
 * Apply ContactFormValues (from ContactInlineForm's save) onto an existing
 * ContactEntry, producing an updated entry for the inspection session list.
 *
 * @param entry The original contact entry.
 * @param values The edited form values.
 * @returns A new ContactEntry with the edited fields applied.
 */
export function applyFormValuesToEntry(
  entry: ContactEntry,
  values: ContactFormValues,
): ContactEntry {
  return {
    ...entry,
    name: values.name,
    roles: (values.defaultRoles ?? []).filter(Boolean),
    phones: (values.phoneNumbers ?? []).map((p) => ({
      id: p._id ?? crypto.randomUUID(),
      number: p.number,
      label: resolveLookupDisplayValue(p.type, true),
    })),
    emails: (values.emails ?? []).map((e) => ({
      id: e._id ?? crypto.randomUUID(),
      address: e.email,
      label: resolveLookupDisplayValue(e.type, true),
    })),
    phone: values.phoneNumbers?.[0]?.number ?? '',
    email: values.emails?.[0]?.email ?? '',
  };
}
